import history from 'utils/history';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
	ButtonDropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'reactstrap';
import styled from 'styled-components';

import { headerStyle } from 'containers/App/constants';
import { getAttendanceColor } from 'utils/helpers';

import AttendanceBlock from './AttendanceBlock';
import GoogleEventBlock from './GoogleEventBlock';
import './style.css';

const Day = styled.div`
	border: 1px solid #aedef5;
	min-height: 180px;

	div:hover .group-data {
		color: white;
	}
`;

const LegendContainer = styled.div`
	position: absolute;
	top: 1rem;
	left: 0.5rem;
	font-size: 14px;
`;

const ColorBlockContainer = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 4px;
`;

const ColorSquare = styled.div`
	background: ${(props) => getAttendanceColor(props.type)};
	width: 20px;
	height: 20px;
	margin-right: 8px;
	flex-shrink: 0;
`;

const ColorLabel = styled.span`
	color: ${(props) => getAttendanceColor(props.type)};
`;

const ColorBlock = ({ type, label }) => (
	<ColorBlockContainer>
		<ColorSquare type={type} />
		<ColorLabel type={type}>{label || type}</ColorLabel>
	</ColorBlockContainer>
);

ColorBlock.propTypes = {
	type: PropTypes.string,
	label: PropTypes.string,
};

const MonthCalendar = (props) => {
	const { events = [], type = 'patient', headerAction } = props;
	const [monthOptions, setMonths] = useState([]);
	const [selectedMonth, setMonth] = useState({});
	const [monthIndex, setMonthIndex] = useState(0);
	const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

	useEffect(() => {
		let firstMonth = moment('12-31-3030');
		let lastMonth = moment('01-01-1890');

		if (!Array.isArray(events) || events.length === 0) {
			setMonths([]);
			return;
		}

		events.forEach((s) => {
			const startDateAsMoment = moment(s.startDate);
			if (
				startDateAsMoment.isValid() &&
				startDateAsMoment.isBefore(firstMonth)
			) {
				firstMonth = startDateAsMoment;
			}
			const endDateAsMoment = moment(s.endDate);
			if (endDateAsMoment.isValid() && endDateAsMoment.isAfter(lastMonth)) {
				lastMonth = endDateAsMoment;
			}
		});

		const newOptions = [];
		const currentMonth = firstMonth.clone().startOf('month');
		let index = 0;
		let foundCurrentMonth = false;

		while (currentMonth.isBefore(lastMonth)) {
			newOptions.push({
				label: currentMonth.format('MMMM YYYY'),
				value: currentMonth.format('YYYY-MM'),
			});
			if (moment().isSame(currentMonth, 'month')) {
				setMonthIndex(index);
				foundCurrentMonth = true;
			}
			currentMonth.add(1, 'month');
			index++;
		}

		if (!foundCurrentMonth) {
			setMonthIndex(newOptions.length > 0 ? newOptions.length - 1 : 0);
		}

		setMonths(newOptions);
	}, [events]);

	useEffect(() => {
		const newMonth = monthOptions[monthIndex];
		if (newMonth) {
			setMonth(newMonth);
		}
	}, [monthIndex, monthOptions]);

	const prevMonth = () => {
		if (monthIndex > 0) {
			setMonthIndex(monthIndex - 1);
		}
	};

	const nextMonth = () => {
		if (monthIndex < monthOptions.length - 1) {
			setMonthIndex(monthIndex + 1);
		}
	};

	const toggleMonthDropdown = () => {
		setMonthDropdownOpen(!monthDropdownOpen);
	};

	const goto = (route) => {
		history.push({
			pathname: `/${type}/dashboard/${route}`,
		});
	};

	const renderMonth = useCallback(() => {
		if (!selectedMonth.value) {
			return null;
		}

		const noDays = moment(selectedMonth.value).daysInMonth();
		const monthStart = moment(selectedMonth.value).startOf('month').day();
		const noWeeks = Math.ceil((noDays + monthStart) / 7);
		const timeFormat = 'h:mma';

		const dates = [];
		for (let i = 1; i <= noDays; i++) {
			dates.push({ date: i, events: [] });
		}

		events.forEach((event) => {
			const localStart = moment.utc(event.startDate).local();
			if (localStart.isSame(moment(selectedMonth.value), 'month')) {
				const day = localStart.date();
				if (dates[day - 1]) {
					dates[day - 1].events.push({
						...event,
						startTime: localStart.format(timeFormat),
						endTime: moment.utc(event.endDate).local().format(timeFormat),
					});
				}
			}
		});

		const weeks = new Array(noWeeks).fill(null);
		let dateIndex = 0;
		const days = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];

		return (
			<div>
				<div className="row">
					{days.map((d, i) => (
						<div
							key={d}
							className={`${
								i === 0 || i === 6 ? 'col-sm-1' : 'col'
							} p-0 text-center`}
							style={headerStyle}
						>
							{d}
						</div>
					))}
				</div>
				{weeks.map((_, weekIndex) => (
					// eslint-disable-next-line react/no-array-index-key
					<div className="row" key={weekIndex}>
						{Array(7)
							.fill(null)
							.map((__, dayIndex) => {
								let dateData = { events: [] };
								if (weekIndex === 0 && dayIndex < monthStart) {
									// Empty cell before month starts
								} else if (dateIndex < noDays) {
									dateData = dates[dateIndex];
									dateData.events.sort((a, b) =>
										moment(a.startTime, timeFormat).isBefore(
											moment(b.startTime, timeFormat),
										)
											? -1
											: 1,
									);
									dateIndex++;
								}

								return (
									<Day
										className={`${
											dayIndex === 0 || dayIndex === 6 ? 'col-sm-1' : 'col'
										} p-0`}
										// eslint-disable-next-line react/no-array-index-key
										key={dayIndex}
									>
										<div className="m-2" style={headerStyle}>
											{dateData.date}
										</div>
										{dateData.events.map((ev, i) => (
											// eslint-disable-next-line react/no-array-index-key
											<div key={i}>
												{ev.type === 'session' ? (
													<AttendanceBlock
														data={{
															attendance: { status: ev.status },
															program: {
																...ev,
																groupName: ev.title,
															},
														}}
														goto={goto}
													/>
												) : (
													<GoogleEventBlock event={ev} />
												)}
											</div>
										))}
									</Day>
								);
							})}
					</div>
				))}
			</div>
		);
	}, [selectedMonth, events]);

	return (
		<div className="container-fluid bg-white">
			{monthOptions.length === 0 || !selectedMonth ? (
				<div className="text-center w-100 font-30 py-3 pb-0 uppercase">
					No data to display
				</div>
			) : (
				<div
					className="row w-100 mx-2 justify-content-center pt-3 pb-0"
					style={{ fontSize: '34px', position: 'relative' }}
				>
					<LegendContainer className="d-flex">
						<div>
							<ColorBlock type="Present" label="Group Session" />
						</div>
						<div className="ml-4">
							<ColorBlock
								type="GoogleCalendarEvent"
								label="Individual Session"
							/>
						</div>
					</LegendContainer>
					<button
						type="button"
						aria-label="Previous month"
						className="custom-arrow left large hand"
						style={{
							marginTop: '22px',
							marginRight: '4px',
							borderColor:
								monthIndex > 0
									? 'var(--main-color)'
									: 'var(--main-light-grey)',
						}}
						onClick={prevMonth}
					/>
					<ButtonDropdown
						isOpen={monthDropdownOpen}
						toggle={toggleMonthDropdown}
						className="month-dropdown"
					>
						<DropdownToggle>{selectedMonth.label}</DropdownToggle>
						<DropdownMenu
							flip={false}
							style={{ maxHeight: 400, overflow: 'auto' }}
						>
							{monthOptions.map((o, i) => (
								<DropdownItem
									key={o.value}
									className={`month-item ${
										o.value === selectedMonth.value ? 'active' : ''
									}`}
									onClick={() => setMonthIndex(i)}
								>
									{o.label}
								</DropdownItem>
							))}
						</DropdownMenu>
					</ButtonDropdown>
					<button
						type="button"
						aria-label="Next month"
						className="custom-arrow right large hand"
						style={{
							marginTop: '22px',
							marginLeft: '4px',
							borderColor:
								monthIndex < monthOptions.length - 1
									? 'var(--main-color)'
									: 'var(--main-light-grey)',
						}}
						onClick={nextMonth}
					/>
					{headerAction && (
						<div
							style={{
								position: 'absolute',
								right: '1rem',
								top: 0,
								bottom: 0,
								display: 'flex',
								alignItems: 'center',
							}}
						>
							{headerAction}
						</div>
					)}
				</div>
			)}
			<div className="p-4">{renderMonth()}</div>
		</div>
	);
};

MonthCalendar.propTypes = {
	events: PropTypes.array,
	type: PropTypes.string,
	headerAction: PropTypes.node,
};

export default MonthCalendar;
